import { VStack } from '@chakra-ui/layout';
import { Grid, Stack, Text } from '@chakra-ui/react';
import { Response } from 'datx-jsonapi';
import { useResourcesInfinite } from 'datx-jsonapi-react';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';

import { LectureCardSkeleton } from '@/components/ui/shared/lecture/LectureCard/LectureCardSkeleton/LectureCardSkeleton';
import { LectureSearchCard } from '@/components/ui/shared/lecture/LectureSearchCard/LectureSearchCard';
import { NoResults } from '@/components/ui/shared/messages/NoResults/NoResults';
import { useAnalytics } from '@/context/Analytics';
import { SearchGroup } from '@/enums';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { queryLecturesByQueryInfinite } from '@/queries/thin-lectures';
import { ThinLecture } from '@/resources/ThinLecture';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';

import { DEFAULT_PAGE_SIZE, searchGroups } from './constants';

interface ILectureURLSearchParams {
  query?: string;
  eventId?: string;
}

export const LectureSearchResults: FC = () => {
  const { query, eventId } = useQueryParams<ILectureURLSearchParams>({
    query: StringParam,
    eventId: StringParam,
    searchGroup: StringParam,
  });

  const analytics = useAnalytics();
  const { t } = useTranslation();

  const filters = useMemo(() => {
    return {
      event_id: eventId,
      // searchable_attribute: SearchGroup.LECTURE,
      query: query || '',
    } as Record<string, string>;
  }, [eventId, query]);

  const { data, error, size, setSize } = useResourcesInfinite(
    queryLecturesByQueryInfinite({ filters, sort: 'algolia_order', pageSize: DEFAULT_PAGE_SIZE })
  );

  const { items, isLoadingMore, isReachingEnd, isEmpty, meta } = useMemo(
    () => extractSWRInfiniteData<ThinLecture>(data as Array<Response<ThinLecture>>, error, size),
    [data, error, size]
  );
  const totalCount = meta?.totalCount;

  useEffect(() => {
    if (!meta || !filters.query) {
      return;
    }

    analytics.track('event_calendar_search', {
      event_id: eventId,
      query: filters.query,
      lectures_num: meta.totalCount,
    });
  }, [analytics, eventId, filters.query, meta]);

  const searchGroupLabel = searchGroups.find((sg) => sg.id === SearchGroup.LECTURE).value.toLowerCase();

  if (isEmpty) {
    return (
      <VStack align={'start'} p={8}>
        <NoResults title={query ? t('common.noResultsForQuery', { query }) : t('common.noResults')} />
        <Text textStyle="plexBody">{t('schedule.search.notFound', { searchGroup: searchGroupLabel })}</Text>
      </VStack>
    );
  }

  if (!data?.length) {
    return (
      <Grid templateColumns="repeat(auto-fill, minmax(min(100%, 276px), 1fr))" gap="8" p={8}>
        {Array.from({ length: DEFAULT_PAGE_SIZE }, (_, index) => (
          <LectureCardSkeleton key={index} />
        ))}
      </Grid>
    );
  }

  return (
    <Stack spacing="20px" p={6}>
      {!isLoadingMore && <Text textStyle="plexBody">{totalCount} results</Text>}
      <Grid gridTemplateColumns="repeat(auto-fill, min(100%, 290px))" gap="6">
        {items.map((lecture) => (
          <LectureSearchCard key={lecture.id} eventId={eventId} lecture={lecture} />
        ))}
        {isLoadingMore
          ? Array.from({ length: DEFAULT_PAGE_SIZE }, (_, index) => <LectureCardSkeleton key={index} />)
          : null}
      </Grid>
      <InView
        onChange={(inView) => {
          if (inView && !error && !isReachingEnd) {
            setSize(size + 1);
          }
        }}
      />
    </Stack>
  );
};
